import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, addMonths, isBefore, startOfDay } from "date-fns";
import { Calendar as CalendarIcon, Clock, User, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { captureEvent } from "../lib/posthog";
import { cn } from "../lib/utils";

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    m.addEventListener("change", onChange);
    return () => m.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

// Time slots: one hour apart, 9 AM to 5 PM (9, 10, 11, 12, 1, 2, 3, 4, 5)
const TIME_SLOTS = [
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
];

export default function Booking() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [step, setStep] = useState(1); // 1: details, 2: date or project details, 3: time (narrow only)
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [unableToMeet, setUnableToMeet] = useState(false);
  const [projectForm, setProjectForm] = useState({
    message: "",
    company: "",
    projectType: "",
    budget: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMode, setSuccessMode] = useState("booking"); // 'booking' | 'inquiry'
  const [errors, setErrors] = useState({});
  const isWideLayout = useMediaQuery("(min-width: 1024px)");

  const fromOffer = searchParams.get("from") === "offer";

  useEffect(() => {
    captureEvent("booking_page_viewed", {
      from_offer: fromOffer,
      referrer: typeof document !== "undefined" ? document.referrer || undefined : undefined,
    });
  }, [fromOffer]);

  const disabledDates = (date) => {
    const today = startOfDay(new Date());
    return isBefore(date, today);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime("");
  };

  const canProceedFromDate = selectedDate != null;
  const canProceedFromTime = selectedTime !== "";
  const canSubmitDetails =
    form.name?.trim() &&
    form.email?.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.phone?.trim();

  const canSubmitInquiry = projectForm.message?.trim()?.length > 0;

  const handleNext = () => {
    if (step === 1) {
      captureEvent("booking_details_completed", { from_offer: fromOffer });
      setStep(2);
    } else if (step === 2) {
      if (unableToMeet) {
        if (canSubmitInquiry) submitInquiry();
      } else {
        if (isWideLayout && canProceedFromDate && canProceedFromTime) {
          captureEvent("booking_date_time_selected", { from_offer: fromOffer });
          submitBooking();
        } else if (!isWideLayout) {
          captureEvent("booking_date_selected", { from_offer: fromOffer });
          setStep(3);
        }
      }
    } else if (step === 3 && canProceedFromTime) {
      captureEvent("booking_date_time_selected", { from_offer: fromOffer });
      submitBooking();
    }
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, submit: undefined }));
  };

  const submitBooking = async () => {
    setErrors({});
    setIsSubmitting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const res = await fetch(`${apiUrl}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          date: selectedDate?.toISOString(),
          time: selectedTime,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Booking failed");
      captureEvent("booking_submitted", {
        from_offer: fromOffer,
        date: selectedDate?.toISOString(),
        time: selectedTime,
      });
      setSuccessMode("booking");
      setIsSuccess(true);
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitInquiry = async () => {
    setErrors({});
    setIsSubmitting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const res = await fetch(`${apiUrl}/api/booking/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          message: projectForm.message.trim(),
          company: projectForm.company?.trim() || undefined,
          projectType: projectForm.projectType?.trim() || undefined,
          budget: projectForm.budget?.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Inquiry failed");
      captureEvent("booking_inquiry_submitted", {
        from_offer: fromOffer,
        has_company: !!projectForm.company?.trim(),
        has_project_type: !!projectForm.projectType?.trim(),
        has_budget: !!projectForm.budget?.trim(),
      });
      setSuccessMode("inquiry");
      setIsSuccess(true);
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          {successMode === "inquiry" ? (
            <>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Thanks for sharing your project</h1>
              <p className="text-gray-600 mb-6">
                We've received your details and will be in touch soon at {form.email}.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">You're scheduled!</h1>
              <p className="text-gray-600 mb-6">
                Your meeting is set for <strong>{selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}</strong> at{" "}
                <strong>{selectedTime}</strong>. We sent a confirmation to {form.email}.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa] text-gray-900">
      {/* Top bar - Calendly style */}
      <header className="border-b border-gray-200 bg-white pt-safe-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="text-sm text-gray-500">Axios Development</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left column - event info (Calendly-style) */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="lg:sticky lg:top-8">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
                30 min Meeting
              </h1>
              <p className="text-gray-500 text-sm sm:text-base mb-6">
                A quick call to discuss your project and how we can help.
              </p>
              <div className="flex items-center gap-3 text-gray-600 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>30 min</span>
              </div>
            </div>
          </div>

          {/* Right column - calendar, time, form */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Step indicator */}
              <div className="border-b border-gray-100 px-6 py-4 flex items-center gap-2 text-sm text-gray-500">
                <span
                  className={cn(
                    "font-medium",
                    step >= 1 ? "text-gray-900" : "text-gray-400"
                  )}
                >
                  Your details
                </span>
                <span className="text-gray-300">›</span>
                <span
                  className={cn(
                    "font-medium",
                    step >= 2 ? "text-gray-900" : "text-gray-400"
                  )}
                >
                  {unableToMeet ? "Project details" : "Date"}
                </span>
                {!unableToMeet && (
                  <>
                    <span className="text-gray-300">›</span>
                    <span
                      className={cn(
                        "font-medium",
                        step >= 3 ? "text-gray-900" : "text-gray-400"
                      )}
                    >
                      Time
                    </span>
                  </>
                )}
              </div>

              <div className="p-6 sm:p-8">
                {step === 1 && (
                  <>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#0066ff]" />
                      Enter your details
                    </h2>
                    {errors.submit && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg mb-4">
                        {errors.submit}
                      </p>
                    )}
                    <div className="space-y-5">
                      <div>
                        <Label htmlFor="name" className="text-gray-700 font-medium">
                          Name
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={form.name}
                          onChange={handleInputChange}
                          placeholder="Your name"
                          required
                          className={cn(
                            "mt-1.5 h-11 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]",
                            errors.name && "border-red-500"
                          )}
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-gray-700 font-medium">
                          Email
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleInputChange}
                          placeholder="you@company.com"
                          required
                          className={cn(
                            "mt-1.5 h-11 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]",
                            errors.email && "border-red-500"
                          )}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-gray-700 font-medium">
                          Phone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleInputChange}
                          placeholder="+1 (555) 000-0000"
                          required
                          className={cn(
                            "mt-1.5 h-11 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]",
                            errors.phone && "border-red-500"
                          )}
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 pt-2">
                        <Checkbox
                          id="unableToMeet"
                          checked={unableToMeet}
                          onCheckedChange={(checked) => {
                            const value = !!checked;
                            setUnableToMeet(value);
                            if (value) captureEvent("booking_unable_to_meet_checked", { from_offer: fromOffer });
                          }}
                        />
                        <Label
                          htmlFor="unableToMeet"
                          className="text-sm font-normal text-gray-700 cursor-pointer"
                        >
                          I'm unable to meet — I'd rather share my project details instead
                        </Label>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <Button
                        onClick={() => setStep(2)}
                        disabled={!canSubmitDetails}
                        className="rounded-full bg-[#0066ff] hover:bg-[#0052cc] text-white px-6"
                      >
                        Next
                      </Button>
                    </div>
                  </>
                )}

                {step === 2 && unableToMeet && (
                  <>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#0066ff]" />
                      Tell us about your project
                    </h2>
                    {errors.submit && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg mb-4">
                        {errors.submit}
                      </p>
                    )}
                    <div className="space-y-5">
                      <div>
                        <Label htmlFor="message" className="text-gray-700 font-medium">
                          Project details <span className="text-gray-400">(required)</span>
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={projectForm.message}
                          onChange={handleProjectFormChange}
                          placeholder="Describe your project, goals, timeline, or what you need help with..."
                          required
                          rows={5}
                          className={cn(
                            "mt-1.5 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]",
                            errors.submit && "border-red-500"
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-gray-700 font-medium">
                          Company
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={projectForm.company}
                          onChange={handleProjectFormChange}
                          placeholder="Your company (optional)"
                          className="mt-1.5 h-11 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="projectType" className="text-gray-700 font-medium">
                          Project type
                        </Label>
                        <Input
                          id="projectType"
                          name="projectType"
                          type="text"
                          value={projectForm.projectType}
                          onChange={handleProjectFormChange}
                          placeholder="e.g. Website, E‑commerce, App (optional)"
                          className="mt-1.5 h-11 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="budget" className="text-gray-700 font-medium">
                          Budget
                        </Label>
                        <Input
                          id="budget"
                          name="budget"
                          type="text"
                          value={projectForm.budget}
                          onChange={handleProjectFormChange}
                          placeholder="Optional"
                          className="mt-1.5 h-11 rounded-lg border-gray-200 focus:ring-2 focus:ring-[#0066ff]/20 focus:border-[#0066ff]"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-between">
                      <Button
                        variant="ghost"
                        onClick={handleBack}
                        className="rounded-full text-gray-600"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleNext}
                        disabled={!canSubmitInquiry || isSubmitting}
                        className="rounded-full bg-[#0066ff] hover:bg-[#0052cc] text-white px-6"
                      >
                        {isSubmitting ? "Sending…" : "Submit"}
                      </Button>
                    </div>
                  </>
                )}

                {step === 2 && !unableToMeet && (
                  <>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-[#0066ff]" />
                      Select a date
                    </h2>
                    <div
                      className={cn(
                        "flex flex-col gap-6",
                        isWideLayout && "lg:flex-row lg:items-start lg:gap-8",
                        !isWideLayout && "w-full max-w-2xl mx-auto"
                      )}
                    >
                      <div
                        className={cn(
                          "flex justify-center sm:block booking-calendar [&_.booking-selected]:!bg-transparent [&_.booking-selected_button]:!text-white [&_.booking-selected_button]:!bg-[#0066ff] [&_.booking-selected_button]:!rounded-full [&_.booking-today_button]:!text-[#0066ff] [&_.booking-today_button]:!bg-[#0066ff]/15 [&_.booking-today_button]:!rounded-full",
                          !isWideLayout && "w-full flex justify-center"
                        )}
                      >
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={handleDateSelect}
                          disabled={disabledDates}
                          startMonth={new Date()}
                          endMonth={addMonths(new Date(), 3)}
                          navLayout="around"
                          className={cn(
                            "rounded-xl border border-gray-200 bg-white inline-block shadow-sm",
                            isWideLayout ? "p-4" : "p-6 sm:p-8 w-full max-w-md sm:max-w-lg mx-auto"
                          )}
                          classNames={{
                            root: "!relative",
                            months: "flex flex-col",
                            month: isWideLayout ? "space-y-3" : "space-y-4",
                            month_caption: cn(
                              "flex justify-center items-center relative",
                              isWideLayout ? "h-10" : "h-12"
                            ),
                            caption_label: isWideLayout
                              ? "text-base font-semibold text-gray-900"
                              : "text-lg sm:text-xl font-semibold text-gray-900",
                            nav: "flex items-center justify-between gap-2",
                            button_previous: cn(
                              "absolute left-0 top-0 rounded-lg inline-flex items-center justify-center hover:bg-gray-100 text-gray-600 disabled:opacity-40",
                              isWideLayout ? "h-9 w-9" : "h-10 w-10 sm:h-11 sm:w-11"
                            ),
                            button_next: cn(
                              "absolute right-0 top-0 rounded-lg inline-flex items-center justify-center hover:bg-gray-100 text-gray-600 disabled:opacity-40",
                              isWideLayout ? "h-9 w-9" : "h-10 w-10 sm:h-11 sm:w-11"
                            ),
                            month_grid: "w-full border-collapse",
                            weekdays: "border-b border-gray-100",
                            weekday: isWideLayout
                              ? "w-10 py-2 text-xs font-medium text-gray-500 text-center"
                              : "w-11 sm:w-12 py-2.5 text-sm font-medium text-gray-500 text-center",
                            day: isWideLayout
                              ? "w-10 h-10 p-0 text-center align-middle"
                              : "w-11 h-11 sm:w-12 sm:h-12 p-0 text-center align-middle",
                            day_button: cn(
                              "rounded-full inline-flex items-center justify-center font-medium text-gray-700 hover:bg-[#0066ff]/10 focus:outline-none focus:ring-2 focus:ring-[#0066ff]/30",
                              isWideLayout
                                ? "h-10 w-10 text-sm"
                                : "h-11 w-11 sm:h-12 sm:w-12 text-base"
                            ),
                            selected: "booking-selected",
                            today: "booking-today !bg-transparent",
                            outside: "text-gray-300",
                            disabled: "!text-gray-300 !opacity-60 cursor-not-allowed",
                          }}
                        />
                      </div>

                      {isWideLayout && (
                        <div className="lg:w-52 lg:shrink-0 lg:border lg:border-gray-200 lg:rounded-xl lg:bg-gray-50/50 lg:overflow-hidden">
                          <h3 className="text-sm font-semibold text-gray-700 px-3 py-2 border-b border-gray-200">
                            <Clock className="w-4 h-4 inline mr-1.5 text-[#0066ff]" />
                            {selectedDate
                              ? format(selectedDate, "MMM d")
                              : "Select a date"}
                          </h3>
                          <div className="max-h-64 overflow-y-auto p-2 flex flex-col gap-1">
                            {selectedDate ? (
                              TIME_SLOTS.map((time) => (
                                <button
                                  key={time}
                                  type="button"
                                  onClick={() => setSelectedTime(time)}
                                  className={cn(
                                    "w-full py-2.5 px-3 rounded-lg text-sm font-medium text-left transition-colors shrink-0",
                                    selectedTime === time
                                      ? "bg-[#0066ff] text-white"
                                      : "text-gray-700 hover:bg-gray-200"
                                  )}
                                >
                                  {time}
                                </button>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500 py-4 px-2 text-center">
                                Pick a date to see times
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-6 flex justify-between">
                      <Button
                        variant="ghost"
                        onClick={handleBack}
                        className="rounded-full text-gray-600"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleNext}
                        disabled={
                          isSubmitting ||
                          (isWideLayout
                            ? !canProceedFromDate || !canProceedFromTime
                            : !canProceedFromDate)
                        }
                        className="rounded-full bg-[#0066ff] hover:bg-[#0052cc] text-white px-6"
                      >
                        {isWideLayout && canProceedFromDate && canProceedFromTime && isSubmitting
                          ? "Scheduling…"
                          : isWideLayout && canProceedFromDate && canProceedFromTime
                            ? "Schedule Event"
                            : "Next"}
                      </Button>
                    </div>
                  </>
                )}

                {step === 3 && (
                  <>
                    <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-[#0066ff]" />
                      Select a time
                    </h2>
                    <p className="text-gray-500 text-sm mb-4">
                      {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {TIME_SLOTS.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setSelectedTime(time)}
                          className={cn(
                            "py-3 px-4 rounded-lg text-sm font-medium transition-colors",
                            selectedTime === time
                              ? "bg-[#0066ff] text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          )}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                    {errors.submit && (
                      <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg mb-4">
                        {errors.submit}
                      </p>
                    )}
                    <div className="mt-6 flex justify-between">
                      <Button
                        variant="ghost"
                        onClick={handleBack}
                        className="rounded-full text-gray-600"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleNext}
                        disabled={!canProceedFromTime || isSubmitting}
                        className="rounded-full bg-[#0066ff] hover:bg-[#0052cc] text-white px-6"
                      >
                        {isSubmitting ? "Scheduling…" : "Schedule Event"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
